import type { ReactNode } from "react";

/**
 * Explicit empty / loading / error views (ARCHITECTURE §7). Every data surface
 * owns all four states — never a blank box. On error, we show no figures rather
 * than an unverified balance.
 */

export function LoadingSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="card">
      <div className="card-body" aria-busy="true" aria-live="polite">
        <span className="tiny muted">Loading…</span>
        {Array.from({ length: rows }, (_, i) => (
          <div className="skeleton-row" key={i}>
            <div className="sk" />
            <div className="sk" />
            <div className="sk" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function EmptyState({
  glyph = "▦",
  title,
  message,
  children,
}: {
  glyph?: string;
  title: string;
  message: string;
  children?: ReactNode;
}) {
  return (
    <div className="card">
      <div className="state empty">
        <div className="glyph" aria-hidden="true">
          {glyph}
        </div>
        <h3>{title}</h3>
        <p>{message}</p>
        {children ? <div className="row" style={{ justifyContent: "center" }}>{children}</div> : null}
      </div>
    </div>
  );
}

export function ErrorState({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="card">
      <div className="state error" role="alert">
        <div className="glyph" aria-hidden="true">
          ⚠
        </div>
        <h3>{title}</h3>
        <p>{message}</p>
        {onRetry ? (
          <button type="button" className="btn secondary" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </div>
    </div>
  );
}
